import React, { forwardRef, useCallback } from 'react';
import clsx from 'clsx';

export interface ToggleProps {
  /** Whether the toggle is on */
  checked: boolean;
  /** Callback when toggled */
  onChange: (checked: boolean) => void;
  /** Disable the toggle */
  disabled?: boolean;
  /** Toggle track size */
  size?: 'sm' | 'md';
  /** Accessible label text */
  label?: string;
  /** Additional class names on the wrapper */
  className?: string;
}

const trackSizes: Record<NonNullable<ToggleProps['size']>, string> = {
  sm: 'w-9 h-5',
  md: 'w-11 h-6',
};

const thumbSizes: Record<NonNullable<ToggleProps['size']>, string> = {
  sm: 'h-3.5 w-3.5',
  md: 'h-4.5 w-4.5',
};

const thumbTranslate: Record<NonNullable<ToggleProps['size']>, { off: string; on: string }> = {
  sm: { off: 'translate-x-0.5', on: 'translate-x-[18px]' },
  md: { off: 'translate-x-0.5', on: 'translate-x-[22px]' },
};

export const Toggle = forwardRef<HTMLButtonElement, ToggleProps>(
  ({ checked, onChange, disabled = false, size = 'md', label, className }, ref) => {
    const handleClick = useCallback(() => {
      if (!disabled) {
        onChange(!checked);
      }
    }, [checked, disabled, onChange]);

    const handleKeyDown = useCallback(
      (e: React.KeyboardEvent) => {
        if (disabled) return;
        if (e.key === ' ' || e.key === 'Enter') {
          e.preventDefault();
          onChange(!checked);
        }
      },
      [checked, disabled, onChange],
    );

    const toggle = (
      <button
        ref={ref}
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={label && !className?.includes('flex') ? label : undefined}
        disabled={disabled}
        onClick={handleClick}
        onKeyDown={handleKeyDown}
        className={clsx(
          'relative inline-flex flex-shrink-0 cursor-pointer rounded-full',
          'transition-colors duration-[var(--volt-transition-fast)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--volt-blue)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--volt-dark)]',
          'disabled:opacity-50 disabled:cursor-not-allowed',
          trackSizes[size],
          checked
            ? 'bg-[var(--volt-blue)]'
            : 'bg-[var(--volt-dark-border)]',
        )}
      >
        <span
          aria-hidden="true"
          className={clsx(
            'inline-block rounded-full bg-white shadow-sm',
            'transform transition-transform duration-[var(--volt-transition-fast)]',
            'mt-[3px]',
            thumbSizes[size],
            checked ? thumbTranslate[size].on : thumbTranslate[size].off,
          )}
        />
      </button>
    );

    if (label) {
      return (
        <label
          className={clsx(
            'inline-flex items-center gap-3 cursor-pointer',
            disabled && 'opacity-50 cursor-not-allowed',
            className,
          )}
        >
          {toggle}
          <span className="text-sm font-medium text-[var(--volt-text-secondary)]">{label}</span>
        </label>
      );
    }

    return toggle;
  },
);

Toggle.displayName = 'Toggle';
